import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { hasFullAccess } from '../lib/platform';
import UpgradeScreen from '../components/UpgradeScreen';
import Logo from '../components/Logo';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, 
  Bot, 
  MapPin, 
  Euro, 
  Ruler, 
  TrendingUp, 
  TrendingDown,
  Loader2,
  Building2,
  Home,
  History,
  Sparkles,
  Target,
  BarChart3,
  CheckCircle2,
  Clock,
  FileText,
  Info,
  ArrowUp,
  ArrowDown,
  PieChart,
  Calendar,
  ExternalLink,
  X,
  ChevronRight,
  Flame,
  Activity,
  DollarSign,
  Users,
  Percent,
  Layers
} from 'lucide-react';

const BiensVendusAgentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isClubMember = hasFullAccess(user?.subscription_tier);

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [results, setResults] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  
  // Transaction detail modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Search criteria
  const [criteria, setCriteria] = useState({
    localisation: '',
    type_bien: 'tous',
    budget_max: '',
    surface_min: '',
    annee_min: '2020',
    rayon: '500'
  });

  // Commune suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCommune, setSelectedCommune] = useState(null);

  // Loading steps
  const loadingSteps = [
    { label: 'Recherche', description: 'Interrogation de la base DVF...' },
    { label: 'Collecte', description: 'Récupération des transactions...' },
    { label: 'Analyse IA', description: 'Analyse des tendances du marché...' },
    { label: 'Graphiques', description: 'Génération des visualisations...' }
  ];

  const loadingTips = [
    "Les données DVF proviennent des notaires et sont actualisées régulièrement",
    "Le prix au m² varie fortement selon le quartier et l'étage",
    "Comparez toujours plusieurs transactions similaires avant d'investir",
    "Un bien vendu rapidement indique souvent un prix attractif",
    "Les ventes en VEFA ont des prix différents du marché ancien",
    "L'évolution des prix sur 5 ans révèle la dynamique du quartier"
  ];

  // Simulate loading progress
  useEffect(() => {
    let stepInterval;
    let messageInterval;
    
    if (loading) {
      stepInterval = setInterval(() => {
        setLoadingStep(prev => {
          if (prev < loadingSteps.length - 1) return prev + 1;
          return prev;
        });
      }, 3500);

      let tipIndex = 0;
      setLoadingMessage(loadingTips[0]);
      messageInterval = setInterval(() => {
        tipIndex = (tipIndex + 1) % loadingTips.length;
        setLoadingMessage(loadingTips[tipIndex]);
      }, 4000);
    } else {
      setLoadingStep(0);
      setLoadingMessage('');
    }

    return () => {
      clearInterval(stepInterval);
      clearInterval(messageInterval);
    };
  }, [loading]);

  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={Building2}
        iconBgClass="from-teal-500 to-emerald-600"
        iconColorClass="text-teal-500"
        buttonGradientClass="from-teal-500 to-emerald-600"
        title="Biens Vendus (DVF)"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Euro, title: "Prix de vente", description: "Consultez les prix de vente réels des biens autour de vous" },
          { icon: TrendingUp, title: "Tendances du marché", description: "Analysez l'évolution des prix par quartier" },
          { icon: BarChart3, title: "Statistiques détaillées", description: "Accédez aux données DVF avec analyses avancées" }
        ]}
      />
    );
  }

  // Search addresses
  const searchAddresses = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=8`);
      const features = response.data.features || [];
      setSuggestions(features.map(f => ({
        label: f.properties.label,
        name: f.properties.name,
        city: f.properties.city,
        postcode: f.properties.postcode,
        citycode: f.properties.citycode,
        context: f.properties.context,
        coordinates: f.geometry.coordinates,
        type: f.properties.type
      })));
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching addresses:', error);
    }
  };

  const handleSelectAddress = (address) => {
    setSelectedCommune({
      code: address.citycode,
      nom: address.city,
      label: address.label,
      coordinates: address.coordinates,
      postcode: address.postcode
    });
    setCriteria({ ...criteria, localisation: address.label });
    setShowSuggestions(false);
  };

  const handleSearch = async () => {
    if (!selectedCommune) {
      toast.error('Veuillez sélectionner une adresse');
      return;
    }

    setLoading(true);
    setResults(null);
    setLoadingStep(0);

    try {
      // Étape 1: Recherche
      await new Promise(resolve => setTimeout(resolve, 1500));
      setLoadingStep(1);

      // Étape 2: Collecte des transactions
      const params = {
        code_commune: selectedCommune.code,
        annee_min: parseInt(criteria.annee_min) || 2020,
        annee_max: 2025,
        limit: 200
      };
      if (criteria.type_bien !== 'tous') params.type_local = criteria.type_bien;
      if (criteria.budget_max) params.valeur_max = parseInt(criteria.budget_max);
      if (criteria.surface_min) params.surface_min = parseInt(criteria.surface_min);

      const transactionsResponse = await axios.get(`${API}/dvf/transactions`, { params });
      await new Promise(resolve => setTimeout(resolve, 1200));
      setLoadingStep(2);

      // Étape 3: Récupérer les stats
      const statsResponse = await axios.get(`${API}/dvf/stats/${selectedCommune.code}`);
      
      // Analyse IA
      const analyseResponse = await axios.post(`${API}/biens-vendus/analyse`, {
        code_insee: selectedCommune.code,
        commune_nom: selectedCommune.nom,
        adresse: selectedCommune.label,
        transactions_count: transactionsResponse.data.total,
        stats: statsResponse.data,
        type_bien: criteria.type_bien,
        budget_max: criteria.budget_max ? parseInt(criteria.budget_max) : null
      }, { withCredentials: true });
      
      await new Promise(resolve => setTimeout(resolve, 1200));
      setLoadingStep(3);

      // Calculer des stats supplémentaires pour les graphiques
      const transactions = transactionsResponse.data.transactions || [];
      const chartData = calculateChartData(transactions);

      // Étape 4: Finalisation
      setResults({
        transactions: transactionsResponse.data,
        stats: statsResponse.data,
        analyse: analyseResponse.data,
        commune: selectedCommune,
        chartData
      });

      // Ajouter à l'historique
      setSearchHistory(prev => [{
        adresse: selectedCommune.label,
        commune: selectedCommune.nom,
        code: selectedCommune.code,
        date: new Date().toISOString(),
        transactions_count: transactionsResponse.data.total
      }, ...prev.slice(0, 4)]);

      toast.success(`${transactionsResponse.data.total} transactions analysées`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  // Calculer les données pour les graphiques
  const calculateChartData = (transactions) => {
    if (!transactions || transactions.length === 0) return null;

    // Prix par année
    const priceByYear = {};
    const countByYear = {};
    const typeDistribution = { Appartement: 0, Maison: 0, Autre: 0 };
    const surfaceRanges = { '< 50m²': 0, '50-80m²': 0, '80-120m²': 0, '> 120m²': 0 };
    const priceRanges = { '< 200k': 0, '200-400k': 0, '400-600k': 0, '> 600k': 0 };

    transactions.forEach(tx => {
      // Par année
      const year = tx.date?.split('-')[0] || '2024';
      if (!priceByYear[year]) {
        priceByYear[year] = [];
        countByYear[year] = 0;
      }
      if (tx.prix_m2 && tx.prix_m2 > 0) {
        priceByYear[year].push(tx.prix_m2);
      }
      countByYear[year]++;

      // Par type
      const type = tx.type_bien?.toLowerCase() || '';
      if (type.includes('appartement')) typeDistribution.Appartement++;
      else if (type.includes('maison')) typeDistribution.Maison++;
      else typeDistribution.Autre++;

      // Par surface
      const surface = tx.surface || 0;
      if (surface < 50) surfaceRanges['< 50m²']++;
      else if (surface < 80) surfaceRanges['50-80m²']++;
      else if (surface < 120) surfaceRanges['80-120m²']++;
      else surfaceRanges['> 120m²']++;

      // Par prix
      const valeur = tx.valeur || 0;
      if (valeur < 200000) priceRanges['< 200k']++;
      else if (valeur < 400000) priceRanges['200-400k']++;
      else if (valeur < 600000) priceRanges['400-600k']++;
      else priceRanges['> 600k']++;
    });

    // Calculer moyennes par année
    const avgPriceByYear = {};
    Object.keys(priceByYear).sort().forEach(year => {
      const prices = priceByYear[year];
      avgPriceByYear[year] = prices.length > 0 
        ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
        : 0;
    });

    // Calculer l'évolution
    const years = Object.keys(avgPriceByYear).sort();
    let evolution = 0;
    if (years.length >= 2) {
      const firstYear = avgPriceByYear[years[0]];
      const lastYear = avgPriceByYear[years[years.length - 1]];
      if (firstYear > 0) {
        evolution = Math.round(((lastYear - firstYear) / firstYear) * 100);
      }
    }

    return {
      avgPriceByYear,
      countByYear,
      typeDistribution,
      surfaceRanges,
      priceRanges,
      evolution,
      totalTransactions: transactions.length
    };
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    if (price >= 1000000) {
      return (price / 1000000).toFixed(2) + ' M€';
    }
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  // Graphique simple en barres (CSS only)
  const SimpleBarChart = ({ data, title, color = 'bg-red-500', maxValue }) => {
    const entries = Object.entries(data);
    const max = maxValue || Math.max(...entries.map(e => e[1]));
    
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
        <div className="space-y-1.5">
          {entries.map(([label, value]) => (
            <div key={label} className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-16 truncate">{label}</span>
              <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${color} transition-all duration-500`}
                  style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }}
                />
              </div>
              <span className="text-xs font-medium text-slate-700 w-12 text-right">
                {typeof value === 'number' && value > 1000 ? `${(value/1000).toFixed(0)}k` : value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Graphique en cercle simple
  const SimplePieChart = ({ data, title }) => {
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    const colors = ['bg-red-500', 'bg-red-400', 'bg-red-300', 'bg-red-200'];
    let currentAngle = 0;
    
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
        <div className="flex items-center gap-4">
          <div className="relative w-24 h-24">
            <svg viewBox="0 0 36 36" className="w-24 h-24 transform -rotate-90">
              {Object.entries(data).map(([label, value], idx) => {
                const percentage = total > 0 ? (value / total) * 100 : 0;
                const dashArray = `${percentage} ${100 - percentage}`;
                const dashOffset = -currentAngle;
                currentAngle += percentage;
                
                return (
                  <circle
                    key={label}
                    cx="18"
                    cy="18"
                    r="15.9155"
                    fill="transparent"
                    stroke={['#ef4444', '#f87171', '#fca5a5', '#fecaca'][idx] || '#fee2e2'}
                    strokeWidth="3.5"
                    strokeDasharray={dashArray}
                    strokeDashoffset={dashOffset}
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-slate-700">{total}</span>
            </div>
          </div>
          <div className="flex-1 space-y-1">
            {Object.entries(data).map(([label, value], idx) => (
              <div key={label} className="flex items-center gap-2 text-xs">
                <div className={`w-3 h-3 rounded ${colors[idx] || 'bg-red-100'}`} />
                <span className="text-slate-600">{label}</span>
                <span className="font-medium text-slate-700 ml-auto">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Title - Rouge */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-lg">
              <Flame className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Agent IA Biens Vendus</h1>
              <p className="text-slate-500">Analyse des transactions immobilières DVF</p>
            </div>
          </div>
          {searchHistory.length > 0 && (
            <Button variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50">
              <History className="h-4 w-4" />
              Historique ({searchHistory.length})
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Search Panel - Thème rouge */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 bg-gradient-to-br from-red-900 to-red-800 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Search className="h-5 w-5 text-red-300" />
                  Critères de recherche
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-4">
                {/* Location */}
                <div className="space-y-2 relative">
                  <Label className="flex items-center gap-2 text-red-200">
                    <MapPin className="h-4 w-4 text-red-400" />
                    Adresse précise
                  </Label>
                  <Input
                    data-testid="dvf-location"
                    placeholder="15 rue de la Paix, Paris 75002..."
                    value={criteria.localisation}
                    onChange={(e) => {
                      setCriteria({ ...criteria, localisation: e.target.value });
                      searchAddresses(e.target.value);
                    }}
                    className="bg-red-950 border-red-700 text-white placeholder:text-red-400"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border max-h-60 overflow-auto">
                      {suggestions.map((address, idx) => (
                        <button
                          key={idx}
                          data-testid={`suggestion-${idx}`}
                          className="w-full px-4 py-3 text-left hover:bg-red-50 flex flex-col text-slate-900 border-b border-slate-100 last:border-0"
                          onClick={() => handleSelectAddress(address)}
                        >
                          <span className="font-medium text-sm">{address.label}</span>
                          <span className="text-xs text-slate-500">{address.context}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Type de bien */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-red-200">
                    <Building2 className="h-4 w-4 text-red-400" />
                    Type de bien
                  </Label>
                  <Select 
                    value={criteria.type_bien}
                    onValueChange={(value) => setCriteria({ ...criteria, type_bien: value })}
                  >
                    <SelectTrigger className="bg-red-950 border-red-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tous">Tous types</SelectItem>
                      <SelectItem value="Appartement">Appartement</SelectItem>
                      <SelectItem value="Maison">Maison</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Budget max */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-red-200">
                    <Euro className="h-4 w-4 text-red-400" />
                    Budget maximum
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="dvf-budget"
                      type="number"
                      placeholder="500000"
                      value={criteria.budget_max}
                      onChange={(e) => setCriteria({ ...criteria, budget_max: e.target.value })}
                      className="bg-red-950 border-red-700 text-white placeholder:text-red-400"
                    />
                    <span className="text-red-300">€</span>
                  </div>
                </div>

                {/* Surface minimum */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-red-200">
                    <Ruler className="h-4 w-4 text-red-400" />
                    Surface minimum
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="dvf-surface"
                      type="number"
                      placeholder="50"
                      value={criteria.surface_min}
                      onChange={(e) => setCriteria({ ...criteria, surface_min: e.target.value })}
                      className="bg-red-950 border-red-700 text-white placeholder:text-red-400"
                    />
                    <span className="text-red-300">m²</span>
                  </div>
                </div>

                {/* Année minimum */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-red-200">
                    <Calendar className="h-4 w-4 text-red-400" />
                    Depuis l'année
                  </Label>
                  <Select 
                    value={criteria.annee_min}
                    onValueChange={(value) => setCriteria({ ...criteria, annee_min: value })}
                  >
                    <SelectTrigger className="bg-red-950 border-red-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2023">2023</SelectItem>
                      <SelectItem value="2022">2022</SelectItem>
                      <SelectItem value="2021">2021</SelectItem>
                      <SelectItem value="2020">2020</SelectItem>
                      <SelectItem value="2019">2019</SelectItem>
                      <SelectItem value="2018">2018</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  data-testid="dvf-submit"
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 mt-4"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Flame className="h-4 w-4 mr-2" />
                      Analyser les ventes
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {/* Loading State - Thème rouge */}
            {loading && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-10">
                  <div className="max-w-md mx-auto">
                    {/* Animated Icon */}
                    <div className="text-center mb-8">
                      <div className="relative mx-auto w-24 h-24 mb-4">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500 to-red-600 opacity-20 animate-ping"></div>
                        <div className="absolute inset-2 rounded-full bg-gradient-to-r from-red-500 to-red-600 opacity-30 animate-pulse"></div>
                        <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center shadow-lg">
                          <Flame className="h-10 w-10 text-red-500 animate-bounce" />
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-red-600 mb-1">Agent IA en action</h3>
                      <p className="text-slate-500 text-sm">Analyse approfondie du marché...</p>
                    </div>

                    {/* Progress Steps - Rouge */}
                    <div className="mb-8">
                      <div className="flex justify-between mb-3">
                        {loadingSteps.map((step, index) => (
                          <div key={index} className="flex flex-col items-center flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1 transition-all duration-500 ${
                              index < loadingStep 
                                ? 'bg-red-500 text-white' 
                                : index === loadingStep 
                                  ? 'bg-red-600 text-white animate-pulse' 
                                  : 'bg-slate-200 text-slate-400'
                            }`}>
                              {index < loadingStep ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            <span className={`text-xs text-center ${
                              index <= loadingStep ? 'text-slate-700 font-medium' : 'text-slate-400'
                            }`}>
                              {step.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Progress Bar - Rouge */}
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-red-400 to-red-600 transition-all duration-1000 ease-out"
                          style={{ width: `${((loadingStep + 1) / loadingSteps.length) * 100}%` }}
                        ></div>
                      </div>
                      
                      <p className="text-center text-sm text-red-600 mt-3 font-medium animate-pulse">
                        {loadingSteps[loadingStep]?.description}
                      </p>
                    </div>

                    {/* Estimated Time - Rouge */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Temps estimé : 15-30 secondes</p>
                          <p className="text-xs text-red-600">L'IA analyse les transactions du marché</p>
                        </div>
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-sm text-slate-600 transition-opacity duration-500">
                        {loadingMessage}
                      </p>
                    </div>

                    {/* What's happening */}
                    <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                      <div className="bg-white border border-red-100 rounded-lg p-3">
                        <Search className="h-5 w-5 text-red-500 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Scan DVF</p>
                      </div>
                      <div className="bg-white border border-red-100 rounded-lg p-3">
                        <BarChart3 className="h-5 w-5 text-red-600 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Calcul stats</p>
                      </div>
                      <div className="bg-white border border-red-100 rounded-lg p-3">
                        <PieChart className="h-5 w-5 text-red-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Graphiques</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results */}
            {results && !loading && (
              <div className="space-y-6">
                {/* Stats Cards - Thème rouge */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="border-0 shadow-md bg-gradient-to-br from-red-500 to-red-600 text-white">
                    <CardContent className="pt-4 text-center">
                      <Activity className="h-6 w-6 mx-auto mb-1 opacity-80" />
                      <p className="text-3xl font-bold">{results.stats.total_transactions}</p>
                      <p className="text-sm text-red-100">Transactions</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <Euro className="h-6 w-6 mx-auto mb-1 text-red-500" />
                      <p className="text-3xl font-bold text-red-600">{results.stats.global?.prix_m2_moyen?.toLocaleString() || 'N/A'}</p>
                      <p className="text-sm text-slate-500">€/m² moyen</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <Building2 className="h-6 w-6 mx-auto mb-1 text-red-400" />
                      <p className="text-3xl font-bold text-slate-700">{results.stats.appartements?.count || 0}</p>
                      <p className="text-sm text-slate-500">Appartements</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-md">
                    <CardContent className="pt-4 text-center">
                      <Home className="h-6 w-6 mx-auto mb-1 text-red-300" />
                      <p className="text-3xl font-bold text-slate-700">{results.stats.maisons?.count || 0}</p>
                      <p className="text-sm text-slate-500">Maisons</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Graphiques automatiques */}
                {results.chartData && (
                  <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-red-500" />
                        Analyse graphique du marché
                        {results.chartData.evolution !== 0 && (
                          <Badge className={results.chartData.evolution > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {results.chartData.evolution > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                            {results.chartData.evolution > 0 ? '+' : ''}{results.chartData.evolution}% depuis {Object.keys(results.chartData.avgPriceByYear)[0]}
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Prix au m² par année */}
                        <SimpleBarChart 
                          data={results.chartData.avgPriceByYear}
                          title="Prix moyen au m² par année"
                          color="bg-red-500"
                        />
                        
                        {/* Nombre de ventes par année */}
                        <SimpleBarChart 
                          data={results.chartData.countByYear}
                          title="Nombre de ventes par année"
                          color="bg-red-400"
                        />
                        
                        {/* Distribution par type */}
                        <SimplePieChart 
                          data={results.chartData.typeDistribution}
                          title="Répartition par type de bien"
                        />
                        
                        {/* Distribution par surface */}
                        <SimpleBarChart 
                          data={results.chartData.surfaceRanges}
                          title="Répartition par surface"
                          color="bg-red-300"
                        />
                      </div>
                      
                      {/* Indicateurs clés */}
                      <div className="mt-6 grid grid-cols-4 gap-3">
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <Percent className="h-5 w-5 text-red-500 mx-auto mb-1" />
                          <p className="text-lg font-bold text-red-600">
                            {results.chartData.evolution > 0 ? '+' : ''}{results.chartData.evolution}%
                          </p>
                          <p className="text-xs text-slate-500">Évolution prix</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <Users className="h-5 w-5 text-red-500 mx-auto mb-1" />
                          <p className="text-lg font-bold text-red-600">
                            {Math.round(results.chartData.totalTransactions / Object.keys(results.chartData.countByYear).length)}
                          </p>
                          <p className="text-xs text-slate-500">Ventes/an moy.</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <Layers className="h-5 w-5 text-red-500 mx-auto mb-1" />
                          <p className="text-lg font-bold text-red-600">
                            {Math.round((results.chartData.typeDistribution.Appartement / results.chartData.totalTransactions) * 100)}%
                          </p>
                          <p className="text-xs text-slate-500">Appartements</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <DollarSign className="h-5 w-5 text-red-500 mx-auto mb-1" />
                          <p className="text-lg font-bold text-red-600">
                            {Object.values(results.chartData.avgPriceByYear).slice(-1)[0]?.toLocaleString() || 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500">€/m² actuel</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AI Analysis */}
                {results.analyse && (
                  <Card className="border-0 shadow-lg border-l-4 border-l-red-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bot className="h-5 w-5 text-red-500" />
                        Analyse IA du marché
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-slate-700 whitespace-pre-line">{results.analyse.summary}</p>
                      </div>
                      {results.analyse.insights && (
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          {results.analyse.insights.map((insight, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-red-50 p-3 rounded-lg">
                              <Info className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-slate-600">{insight}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Transactions List - Cliquables */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-red-500" />
                      Transactions détaillées
                    </CardTitle>
                    <CardDescription>
                      Cliquez sur une transaction pour voir les détails
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[500px] overflow-auto">
                      {results.transactions.transactions?.slice(0, 30).map((tx, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedTransaction(tx)}
                          className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-red-50 hover:border-red-200 border border-transparent transition-all cursor-pointer text-left group"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              tx.type_bien?.toLowerCase().includes('maison') 
                                ? 'bg-red-100' 
                                : 'bg-red-50'
                            }`}>
                              {tx.type_bien?.toLowerCase().includes('maison') ? (
                                <Home className="h-5 w-5 text-red-500" />
                              ) : (
                                <Building2 className="h-5 w-5 text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{tx.type_bien}</p>
                              <p className="text-sm text-slate-500">{tx.surface} m² - {tx.date}</p>
                              {tx.adresse && (
                                <p className="text-xs text-slate-400 truncate max-w-[200px]">{tx.adresse}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-bold text-red-600">{formatPrice(tx.valeur)}</p>
                              <p className="text-sm text-slate-500">{tx.prix_m2?.toLocaleString()} €/m²</p>
                            </div>
                            <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-red-500 transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Empty State */}
            {!loading && !results && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center mx-auto mb-4">
                    <Flame className="h-10 w-10 text-red-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Analysez les biens vendus</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Entrez une adresse précise (ex: "15 rue de la Paix, Paris 75002") pour découvrir les transactions immobilières et obtenir une analyse IA des tendances du marché avec graphiques.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Transaction Detail Modal */}
      <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTransaction?.type_bien?.toLowerCase().includes('maison') ? (
                <Home className="h-5 w-5 text-red-500" />
              ) : (
                <Building2 className="h-5 w-5 text-red-500" />
              )}
              {selectedTransaction?.type_bien}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              {/* Prix principal */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg p-6 text-center">
                <p className="text-sm text-red-100 mb-1">Prix de vente</p>
                <p className="text-4xl font-bold">{formatPrice(selectedTransaction.valeur)}</p>
                <p className="text-red-100 mt-2">
                  soit <span className="font-bold text-white">{selectedTransaction.prix_m2?.toLocaleString()} €/m²</span>
                </p>
              </div>

              {/* Caractéristiques */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <Ruler className="h-5 w-5 text-red-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-700">{selectedTransaction.surface} m²</p>
                  <p className="text-xs text-slate-500">Surface</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <Calendar className="h-5 w-5 text-red-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-700">{selectedTransaction.date}</p>
                  <p className="text-xs text-slate-500">Date de vente</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <MapPin className="h-5 w-5 text-red-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-700 truncate">{results?.commune?.nom}</p>
                  <p className="text-xs text-slate-500">Commune</p>
                </div>
              </div>

              {/* Adresse */}
              {selectedTransaction.adresse && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Adresse</p>
                      <p className="text-sm text-slate-600">{selectedTransaction.adresse}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Comparaison avec le marché */}
              {results?.stats?.global?.prix_m2_moyen && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-red-500" />
                    Comparaison avec le marché
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{selectedTransaction.prix_m2?.toLocaleString()} €</p>
                      <p className="text-xs text-slate-500">Prix/m² de ce bien</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-600">{results.stats.global.prix_m2_moyen?.toLocaleString()} €</p>
                      <p className="text-xs text-slate-500">Prix/m² moyen commune</p>
                    </div>
                  </div>
                  {selectedTransaction.prix_m2 && results.stats.global.prix_m2_moyen && (
                    <div className="mt-3 text-center">
                      {selectedTransaction.prix_m2 < results.stats.global.prix_m2_moyen ? (
                        <Badge className="bg-green-100 text-green-700">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          {Math.round(((results.stats.global.prix_m2_moyen - selectedTransaction.prix_m2) / results.stats.global.prix_m2_moyen) * 100)}% sous le marché
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {Math.round(((selectedTransaction.prix_m2 - results.stats.global.prix_m2_moyen) / results.stats.global.prix_m2_moyen) * 100)}% au-dessus du marché
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Lien DVF */}
              <a
                href={`https://app.dvf.etalab.gouv.fr/?code_insee=${results?.commune?.code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="outline" className="w-full gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Voir sur DVF Etalab
                </Button>
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BiensVendusAgentPage;
